import { Platform } from 'react-native';
import {
  getCatalogRamSnapshotCache,
  type WebCatalogBrandRow,
} from './catalogRamSnapshotCache';
import type {
  BrandRecord,
  BrandFilterState,
} from '../domain/catalogBrandTypes';
import { buildAccentInsensitiveLikeClause, matchesSearchAny } from '../../shared/format/searchText';

export type { BrandRecord, BrandFilterState } from '../domain/catalogBrandTypes';

export const normalizeBrandName = (name: string): string => name.trim();

export const mapLocalBrandRow = (row: {
  id: string;
  organization_id: string;
  name: string;
  code: string | null;
  website: string | null;
  default_markup_percent: number | null;
  is_active: number | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}): BrandRecord => ({
  id: row.id,
  organizationId: row.organization_id,
  name: row.name,
  code: row.code,
  website: row.website,
  defaultMarkupPercent: row.default_markup_percent,
  isActive: row.is_active !== 0 && row.is_active !== null ? true : false,
  deletedAt: row.deleted_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const isWebCatalogRowActive = (value: boolean | number | null | undefined): boolean =>
  value !== false && value !== 0;

export const mapWebBrandRow = (row: WebCatalogBrandRow): BrandRecord => ({
  id: String(row.id),
  organizationId: String(row.organization_id ?? ''),
  name: String(row.name ?? '').trim(),
  code: row.code ?? null,
  website: row.website ?? null,
  defaultMarkupPercent: row.default_markup_percent ?? null,
  isActive: isWebCatalogRowActive(row.is_active),
  deletedAt: row.deleted_at ?? null,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
});

export const filterWebBrandRecords = (
  rows: WebCatalogBrandRow[],
  organizationId: string,
  filterState: BrandFilterState,
  search?: string
): BrandRecord[] => {
  return rows
    .filter((row) => row?.id && String(row.organization_id ?? '') === organizationId)
    .filter((row) => !row.deleted_at)
    .filter((row) => {
      if (filterState === 'active') return isWebCatalogRowActive(row.is_active);
      if (filterState === 'inactive') return !isWebCatalogRowActive(row.is_active);
      return true;
    })
    .filter((row) => matchesSearchAny([row.name, row.code, row.website], search ?? ''))
    .map(mapWebBrandRow)
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
};

export function brandsFromWebCacheOrNull(
  organizationId: string,
  filterState: BrandFilterState,
  search?: string,
  limit?: number,
  offset = 0
): BrandRecord[] | null {
  if (Platform.OS !== 'web') return null;
  const cached = getCatalogRamSnapshotCache();
  if (!cached?.brands.length) return null;
  const filtered = filterWebBrandRecords(cached.brands, organizationId, filterState, search);
  if (typeof limit === 'number') {
    const pageOffset = Math.max(0, offset);
    return filtered.slice(pageOffset, pageOffset + limit);
  }
  return filtered;
}

export const buildBrandSearchClause = (search?: string): { clause: string; params: string[] } =>
  buildAccentInsensitiveLikeClause(['name', "COALESCE(code, '')", "COALESCE(website, '')"], search);

export const toBrandSyncPayload = (brand: BrandRecord, organizationId: string) => ({
  organizationId,
  id: brand.id,
  name: brand.name,
  code: brand.code,
  website: brand.website,
  defaultMarkupPercent: brand.defaultMarkupPercent,
  isActive: brand.isActive,
  createdAt: brand.createdAt ?? undefined,
  updatedAt: brand.updatedAt ?? new Date().toISOString(),
  deletedAt: brand.deletedAt,
});

export const BRAND_SELECT = `id, organization_id, name, code, website, default_markup_percent, is_active, deleted_at, created_at, updated_at`;
